
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Chrome, Loader2 } from "lucide-react";
import DeveloperLogin from "./DeveloperLogin";
import { hasDeveloperAccess } from "@/utils/developerUtils";
import { useAuth } from "@/hooks/useAuth";
import { MunicipalitySelector } from "./MunicipalitySelector";

const Login = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [showDeveloperLogin, setShowDeveloperLogin] = useState(false);
  const [selectedMunicipality, setSelectedMunicipality] = useState<string | null>(null);
  const { user } = useAuth();
  
  // Check for developer access whenever user state changes or on initial load
  useEffect(() => {
    const userEmail = user?.email || null;
    const shouldShowDeveloperLogin = hasDeveloperAccess(userEmail);
    
    console.log('Login component: checking developer access', { 
      userEmail, 
      shouldShowDeveloperLogin,
      hostname: window.location.hostname,
      currentShowDeveloperLogin: showDeveloperLogin,
      isLovableDomain: window.location.hostname.includes('lovable')
    });
    
    setShowDeveloperLogin(shouldShowDeveloperLogin);
  }, [user, showDeveloperLogin]); // Add user and showDeveloperLogin as dependency to re-check when they change

  const handleGoogleLogin = async () => {
    if (!selectedMunicipality) {
      toast.error("Vänligen välj en kommun först", { duration: 3000 });
      return;
    }

    try {
      setIsLoading(true);
      console.log("Initiating Google login with municipality:", selectedMunicipality);
      
      // Store selected municipality for later use
      localStorage.setItem('selected_municipality_id', selectedMunicipality);
      
      // Add municipality_id to the redirect URL
      const redirectUrl = `${window.location.origin}/auth/callback?municipality_id=${selectedMunicipality}`;
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      });

      console.log("SignInWithOAuth response:", { data, error });

      if (error) {
        console.error("Google login error:", error);
        console.log("Error message:", error.message);
        console.log("Error details:", error.stack);
        
        // Kontrollera om det är vårt specifika registreringsfel
        if (error.message?.includes('user_registration_closed') || error.message?.includes('registration_closed')) {
          toast.error("Vi har för närvarande begränsat åtkomsten till fritidskompassen.se. Detta för att upprätthålla maximal säkerhet för våra användare.", {
            duration: 6000
          });
        } else {
          // Andra typer av fel
          toast.error(
            "Det gick inte att logga in med Google. Försök igen.",
            { duration: 4000 }
          );
        }
        throw error;
      }
    } catch (error: any) {
      console.error('Login error details:', error);
      // Vi har redan hanterat felmeddelandet ovan, så vi behöver inte visa något nytt här
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md space-y-6">
      <MunicipalitySelector 
        value={selectedMunicipality}
        onChange={setSelectedMunicipality}
      />
      
      <Button
        type="button"
        variant="outline"
        className="w-full bg-white hover:bg-gray-100 text-gray-700 hover:text-gray-900 border border-gray-300"
        onClick={handleGoogleLogin}
        disabled={isLoading || !selectedMunicipality}
      >
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Loggar in...
          </>
        ) : (
          <>
            <Chrome className="mr-2 h-5 w-5" strokeWidth={1.5} />
            Logga in med Google
          </>
        )}
      </Button>
      
      <p className="text-white text-center text-sm mt-4">
        För att kunna logga in måste du antingen vara en befintlig användare eller ha fått en inbjudan från en administratör.
      </p>

      {showDeveloperLogin && (
        <DeveloperLogin />
      )}
    </div>
  );
};

export default Login;
